import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { supabase } from '../../lib/supabase';
import SafeIcon from '../../common/SafeIcon';

export default function AuthForm({ initialMode = 'login', onToggleMode, allowSignup = true, onSuccess }) {
  const [mode, setMode] = useState(initialMode); // 'login' | 'signup' | 'forgot'
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [fullName, setFullName] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [resetSent, setResetSent] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      if (mode === 'signup') {
        const { error: signUpError } = await supabase.auth.signUp({
          email,
          password,
          options: { data: { full_name: fullName } }
        });
        if (signUpError) throw signUpError;

      } else if (mode === 'login') {
        const { error: signInError } = await supabase.auth.signInWithPassword({
          email,
          password,
        });
        if (signInError) throw signInError;

      } else if (mode === 'forgot') {
        const { error: resetError } = await supabase.auth.resetPasswordForEmail(email, {
          redirectTo: 'https://order.phoenixkabob.com'
        });
        if (resetError) throw resetError;
        setResetSent(true);
      }

      if (onSuccess && mode !== 'forgot') onSuccess();
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const switchMode = (newMode) => {
    setMode(newMode);
    setError('');
    setResetSent(false);
    setEmail('');
    setPassword('');
  };

  const headings = {
    login: { title: 'Welcome Back', subtitle: 'Sign in to track your order.' },
    signup: { title: 'Create Account', subtitle: 'Start your Phoenix Kabob order.' },
    forgot: { title: 'Reset Password', subtitle: "Enter your email and we'll send you a reset link." }
  };

  return (
    <div className="w-full max-w-md mx-auto">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="glass-panel p-8 md:p-10"
      >
        <div className="text-center mb-8">
          <div className="w-16 h-16 bg-blush-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <SafeIcon name={mode === 'forgot' ? 'Mail' : 'Lock'} className="text-2xl text-blush-400" />
          </div>
          <h2 className="text-3xl font-serif text-charcoal">{headings[mode].title}</h2>
          <p className="text-sage-500 mt-2">{headings[mode].subtitle}</p>
        </div>

        {/* Password reset success state */}
        {resetSent ? (
          <div className="space-y-6 text-center">
            <div className="bg-green-50 border border-green-100 rounded-2xl p-6 space-y-2">
              <SafeIcon name="CheckCircle" className="text-3xl text-green-500 mx-auto" />
              <p className="text-green-700 font-semibold">Reset link sent!</p>
              <p className="text-sm text-green-600">
                Check your inbox at <strong>{email}</strong> and follow the link to reset your password.
              </p>
            </div>
            <button
              onClick={() => switchMode('login')}
              className="btn-secondary w-full"
            >
              <SafeIcon name="ArrowLeft" /> Back to Sign In
            </button>
          </div>
        ) : (
          <>
            {error && (
              <div className="bg-red-50 text-red-600 p-4 rounded-xl border border-red-100 text-sm mb-6 text-center">
                {error}
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-5">
              <AnimatePresence mode="wait">
                {mode === 'signup' && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    exit={{ opacity: 0, height: 0 }}
                  >
                    <label className="block text-sm font-medium text-sage-600 mb-1.5 ml-1">Full Name</label>
                    <input
                      type="text"
                      required
                      className="input-field !py-4 !px-5"
                      value={fullName}
                      onChange={(e) => setFullName(e.target.value)}
                    />
                  </motion.div>
                )}
              </AnimatePresence>

              <div>
                <label className="block text-sm font-medium text-sage-600 mb-1.5 ml-1">Email Address</label>
                <input
                  type="email"
                  required
                  className="input-field !py-4 !px-5"
                  placeholder="you@example.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                />
              </div>

              <AnimatePresence mode="wait">
                {mode !== 'forgot' && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    exit={{ opacity: 0, height: 0 }}
                  >
                    <div className="flex justify-between items-center mb-1.5">
                      <label className="block text-sm font-medium text-sage-600 ml-1">Password</label>
                      {mode === 'login' && (
                        <button
                          type="button"
                          onClick={() => switchMode('forgot')}
                          className="text-xs text-blush-400 font-bold hover:text-blush-500 transition-colors"
                        >
                          Forgot password?
                        </button>
                      )}
                    </div>
                    <input
                      type="password"
                      required
                      className="input-field !py-4 !px-5"
                      placeholder="••••••••"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                    />
                  </motion.div>
                )}
              </AnimatePresence>

              <button
                type="submit"
                disabled={loading}
                className="btn-primary w-full flex items-center justify-center gap-3 mt-4"
              >
                {loading ? (
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                ) : (
                  mode === 'login' ? 'Sign In' :
                  mode === 'signup' ? 'Create Account' :
                  'Send Reset Link'
                )}
              </button>
            </form>

            {/* Footer links */}
            <div className="mt-8 text-center border-t border-sage-100 pt-6 space-y-3">
              {mode === 'forgot' ? (
                <button
                  onClick={() => switchMode('login')}
                  className="text-sm text-blush-400 font-bold hover:text-blush-500 transition-colors flex items-center gap-1 mx-auto"
                >
                  <SafeIcon name="ArrowLeft" className="text-xs" /> Back to Sign In
                </button>
              ) : allowSignup ? (
                <p className="text-sage-500 text-sm">
                  {mode === 'login' ? "Don't have an account?" : "Already have an account?"}
                  <button
                    onClick={() => switchMode(mode === 'login' ? 'signup' : 'login')}
                    className="ml-2 text-blush-400 font-bold hover:text-blush-500 transition-colors"
                  >
                    {mode === 'login' ? 'Sign Up' : 'Sign In'}
                  </button>
                </p>
              ) : null}
            </div>
          </>
        )}
      </motion.div>
    </div>
  );
}
